import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import _ from "lodash";

// Contexts
import { BundleContext } from "../../context/BundleProvider";
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import {
  StudyMenuItem,
  ProgressModal,
  EmptyStudyState,
} from "../../components/studyquestion";
import CommonHeader from "../../components/common/CommonHeader";
import PremiumDialog from "../../components/common/PremiumDialog";

// Constants
import { GET_STUDY_QUESTION_MENU, GET_APP_DOMAIN_LIST, IS_TEST_MODE } from "../../../app_constant";
import { STRING, STRINGS } from "../../constant";
import { faGraduationCap } from "@fortawesome/free-solid-svg-icons";
import StatsGrid from "../../components/common/StatsGrid";

import { TrendingUp, BookText, DollarSign, Activity, ShoppingCart, Eye, Heart, Zap } from 'lucide-react';
import { 
  faBook, 
  faBookmark, 
  faCheckCircle,
  faChartLine,
  faLock
} from '@fortawesome/free-solid-svg-icons';

let _studyQuestionsTracked = false;

const StudyQuestion = () => {
  const navigate = useNavigate();
  const { selectedCertificate } = useContext(BundleContext);
  const { questionsList, profile, isSubscribe } = useContext(AuthContext);
  const { theme } = useContext(ThemeContext);
  const { track } = useAnalytics();

  // Track study questions menu view (once per page load)
  useEffect(() => {
    if (_studyQuestionsTracked) return;
    _studyQuestionsTracked = true;
    track('study_questions_menu_viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State management
  const [expandedMenus, setExpandedMenus] = useState(['by_topic']);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedMainMenu, setSelectedMainMenu] = useState(null);
  const [selectedBookmarkTopic, setSelectedBookmarkTopic] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressModalType, setProgressModalType] = useState('question');
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [isProgressTesting, setProgressTesting] = useState(false);

  let STUDY_QUESTION_MENU = GET_STUDY_QUESTION_MENU(selectedCertificate);

  // Filter out "By Topics (Free Plan)" menu item completely for all users
  STUDY_QUESTION_MENU = STUDY_QUESTION_MENU.filter(item => item.key !== 'by_topic_free_model');
  

  // Calculate statistics
  const calculateStats = () => {
    const bookmarkedQuestions = profile?.question_bookmarks || [];
    const progress = profile?.questions_progress || [];
    
    const totalQuestions = questionsList?.length || 0;
    const totalBookmarks = bookmarkedQuestions.length;
    
    // Calculate total free questions for non-subscribed users
    let totalFreeQuestions = 0;
    if (!isSubscribe) {
      const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
      APP_DOMAIN_LIST.forEach(domain => {
        totalFreeQuestions += domain.num_questions || 0;
      });
    }

      const statsData = [
        {
          icon: faBook,
          stat: totalQuestions.toLocaleString(),
          label: 'Total Questions',
          color: theme?.primary?.orange?.[500],
          iconType: 'fontawesome'
        },
        {
          icon: faBookmark,
          stat: totalBookmarks,
          label: 'Bookmarked',
          color: theme?.primary?.orange?.[500],
          iconType: 'fontawesome'
        },
      ];

    // Add Free Questions stat for non-subscribed users
    if (!isSubscribe && totalFreeQuestions > 0) {
      statsData.push({
        icon: faLock,
        stat: totalFreeQuestions.toLocaleString(),
        label: 'Free Questions',
        color: theme?.primary?.green?.[500],
        iconType: 'fontawesome',
      });
    }

    return statsData;
  };

  const stats = calculateStats();

  // Get question count for menu items
  const getQuestionCount = (menuType, item) => {
    if (menuType === "bookmark") {
      const bookmarks = profile?.question_bookmarks || [];
      if (item.domain_name === "All Bookmarks") {
        return questionsList?.filter(q => bookmarks.includes(q.id)).length || 0;
      }
      return questionsList
        ?.filter(q => bookmarks.includes(q.id))
        .filter(q => q.topic_id === item.id).length || 0;
    }
    return questionsList?.filter(q => q.topic_id === item.id).length || 0;
  };

  // Check if topic has progress
  const hasProgress = (setId) => {
    const progress = profile?.questions_progress || [];
    return progress.some(p => p.study_questions_set_id === setId);
  };

  // Handle topic selection
  const handleTopicClick = (item, menu) => {
    // For non-subscribed users clicking on "By Topics", allow access to free questions
    // Don't redirect to chooseplan - they can access free questions
    if (item.requires_subscription && !isSubscribe && menu.free_model !== 1 && menu.key !== "by_topic") {
      navigate('/chooseplan', {
        state: { canSkip: false, source: 'study_menu' }
      });
      return;
    }

    const setId = menu.key === "bookmark" 
      ? (item.domain_name === "All Bookmarks" ? "bookmark" : `bookmark_${item.id}`)
      : item.id + 2;

    // Check for existing progress
    if (hasProgress(setId)) {
      setSelectedTopic(item);
      setSelectedMainMenu(menu);
      setProgressModalType(menu.key === "bookmark" ? "bookmark" : "question");
      setShowProgressModal(true);
    } else {
      navigateToQuestions(item, menu, false);
    }

    
  };

  // Navigate to questions
  const navigateToQuestions = (item, menu, resume = false) => {
    let questions;
    let setId;

    if (menu.key === "bookmark") {
      const bookmarks = profile?.question_bookmarks || [];
      questions = item.domain_name === "All Bookmarks"
        ? questionsList?.filter(q => bookmarks.includes(q.id))
        : questionsList?.filter(q => bookmarks.includes(q.id) && q.topic_id === item.id);
      setId = item.domain_name === "All Bookmarks" ? "bookmark" : `bookmark_${item.id}`;
    } else {
      questions = questionsList?.filter(q => q.topic_id === item.id);
      setId = item.id + 2;
      
      // Apply free model limit for non-subscribed users clicking on "By Topics"
      if (!isSubscribe && menu.key === "by_topic") {
        questions = questions.slice(0, item.num_questions);
      }
    }

    const navigationState = {
      set_id: setId,
      source: "study_menu",
      questions: questions
    };

    // Add last index if resuming
    if (resume) {
      const progress = profile?.questions_progress || [];
      const setProgress = progress.find(p => p.study_questions_set_id === setId);
      if (setProgress) {
        const lastIndex = questions.findIndex(q => q.id === setProgress.last_seen_question_id);
        navigationState.lastIndex = lastIndex === -1 ? 0 : lastIndex;
      }
    }

    navigate('/studyquestions/question', { state: navigationState });
  };

  // Handle special menu items (Random, Bookmarks root, Testing mode)
  const handleSpecialMenuItem = (item, index) => {
    console.log(item, index);
    if (index === 0) { // Bookmarks root
      const bookmarks = profile?.question_bookmarks || [];
      const questions = questionsList?.filter(q => bookmarks.includes(q.id));
      
      if (hasProgress("bookmark")) {
        setSelectedTopic({ domain_name: "All Bookmarks" });
        setSelectedMainMenu(item);
        setProgressModalType("bookmark");
        setShowProgressModal(true);
      } else {
        navigate('/studyquestions/question', {
          state: { set_id: "bookmark", questions: questions }
        });
      }
    } else if (index === 1) { // Random questions
      const questions = _.shuffle(questionsList).slice(0, 10);
      navigate('/studyquestions/question', {
        state: { title: item.title, questions: questions }
      });
    } else if (item.key === 'all_questions_testing') {
      // Check for existing progress
      const progress = profile?.questions_progress || [];
      const setProgress = progress.filter((obj) => {
        return obj.study_questions_set_id === 'all_questions_testing';
      });
      
      // Check if there's progress and the last seen question is different from the first question
      if (setProgress && setProgress.length > 0 && questionsList.length > 0 && questionsList[0].id !== setProgress[0].last_seen_question_id) {
        // Show resume popup
        setProgressTesting(true);
      } else {
        // No progress or at beginning, navigate directly
        navigate('/studyquestions/question', {
          state: {
            set_id: 'all_questions_testing',
            questions: questionsList,
            title: 'All Questions (Testing)',
            testing_mode: true
          }
        });
      }
    }
  };

  // Handle progress modal action
  const handleProgressAction = (startOver) => {
    setShowProgressModal(false);
    navigateToQuestions(selectedTopic, selectedMainMenu, !startOver);
    setSelectedTopic(null);
    setSelectedMainMenu(null);
  };

  // Handle testing mode progress action
  const handleTestingProgressAction = (startOver) => {
    setProgressTesting(false);
    const progress = profile?.questions_progress || [];
    const setProgress = progress.filter((obj) => {
      return obj.study_questions_set_id === 'all_questions_testing';
    });
    const questions = questionsList;
    
    if (startOver) {
      // Start from beginning
      navigate('/studyquestions/question', {
        state: {
          set_id: 'all_questions_testing',
          questions: questions,
          title: 'All Questions (Testing)',
          testing_mode: true
        }
      });
    } else {
      // Resume from where stopped
      if (setProgress.length > 0) {
        const questionIndex = questions.findIndex((obj) => obj.id === setProgress[0].last_seen_question_id);
        navigate('/studyquestions/question', {
          state: {
            set_id: 'all_questions_testing',
            questions: questions,
            lastIndex: questionIndex === -1 ? 0 : questionIndex,
            title: 'All Questions (Testing)',
            testing_mode: true
          }
        });
      }
    }
  };

  // Toggle menu expansion
  const toggleMenu = (menuKey) => {
    setExpandedMenus(prev => 
      prev.includes(menuKey) 
        ? prev.filter(key => key !== menuKey)
        : [...prev, menuKey]
    );
  };

  // Handle premium upgrade
  const handleUpgrade = () => {
    navigate('/chooseplan');
  };

  // Handle locked item click
  const handleLockedItemClick = (item) => {
    setShowPremiumDialog(true);
  };
  


  return (
    <div className="min-h-screen transition-colors duration-300" 
         style={{ background: theme?.background?.main }}>
      <div>
      <CommonHeader
      title="Study Questions"
      icon={faGraduationCap}
      subtitle="Build expertise through focused practice across all domains"
      quote="Consistent practice builds confidence. Strategic practice builds mastery."
      onBack={() => navigate("/home")}
      theme={theme}
      showBackButton={true}
    />
        <div className="pb-4">
        <StatsGrid stats={stats} theme={theme} />
        </div>
        
        {/* Menu Items */}
        <div className="space-y-4">
          {STUDY_QUESTION_MENU.map((menu, index) => (
            <StudyMenuItem
              key={menu.key}
              menu={menu}
              isExpanded={expandedMenus.includes(menu.key)}
              onToggle={() => toggleMenu(menu.key)}
              onItemClick={(item) => handleTopicClick(item, menu)}
              onMenuClick={() => handleSpecialMenuItem(menu, index)}
              getQuestionCount={(item) => getQuestionCount(menu.key, item)}
              isSubscribed={isSubscribe}
              theme={theme}
              onLockedItemClick={handleLockedItemClick}
              questionsList={questionsList}
            />
          ))}
        </div>
        
        {/* Empty State */}
        {STUDY_QUESTION_MENU.length === 0 && (
          <EmptyStudyState theme={theme} />
        )}
      </div>
      
      {/* Progress Modal */}
      <ProgressModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        onStartOver={() => handleProgressAction(true)}
        onResume={() => handleProgressAction(false)}
        title={STRINGS.study_questions}
        message={STRINGS.flashcard_message}
        theme={theme}
      />

      {/* Testing Mode Progress Modal */}
      <ProgressModal
        isOpen={isProgressTesting}
        onClose={() => setProgressTesting(false)}
        onStartOver={() => handleTestingProgressAction(true)}
        onResume={() => handleTestingProgressAction(false)}
        title="All Questions (Testing)"
        message={STRINGS.flashcard_message}
        theme={theme}
      />

      {/* Premium Dialog */}
      <PremiumDialog
        isOpen={showPremiumDialog}
        onClose={() => setShowPremiumDialog(false)}
        onUpgrade={() => {
          setShowPremiumDialog(false);
          handleUpgrade();
        }}
        theme={theme}
      />
    </div>
  );
};

export default StudyQuestion;